import {
	createContext,
	useContext,
	useMemo,
	FC,
	ReactNode,
	useState,
	useCallback,
	useEffect,
} from 'react';
import {
	Application,
	Feedback,
	OtherReferenceFeedback,
	OtherReferenceRecommendationRequest,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { submitOtherReferenceFeedback } from '../../../service/VolunteerApplicationService';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { useAuth } from '../../../hooks/useAuth';
import { INTERVIEW_LANDING } from '../../../shared/Routes';
import { useInterviewWizard } from '../useInterviewWizard';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import useUserSession from '../../../hooks/useUserSession';

export interface OtherReferenceInterviewContextType {
	inspirationAndWillingness: Feedback;
	setInspirationAndWillingness: (feedback: Feedback) => void;
	practice: Feedback;
	setPractice: (feedback: Feedback) => void;
	characterAndHabits: Feedback;
	setCharacterAndHabits: (feedback: Feedback) => void;
	availabilityAfterProgram: Feedback;
	setAvailabilityAfterProgram: (feedback: Feedback) => void;
	support: Feedback;
	setSupport: (feedback: Feedback) => void;
	understandingOfHeartfulness: Feedback;
	setUnderstandingOfHeartfulness: (feedback: Feedback) => void;
	application: Partial<Application>;
	onSubmitClick: (values: Feedback) => void;
	onCancelClick: () => void;
	otherReferencePreceptorRecommendation: OtherReferenceFeedback;
	setOtherReferencePreceptorRecommendation: (
		feedback: OtherReferenceFeedback
	) => void;
	otherReferenceRecord: OtherReferenceFeedback;
	setOtherReferenceRecord: (feedback: OtherReferenceFeedback) => void;
}

interface Props {
	children: ReactNode;
}

const OtherReferenceInterviewContext =
	createContext<OtherReferenceInterviewContextType>({
		inspirationAndWillingness: {},
		setInspirationAndWillingness: () => {},
		practice: {},
		setPractice: () => {},
		characterAndHabits: {},
		setCharacterAndHabits: () => {},
		availabilityAfterProgram: {},
		setAvailabilityAfterProgram: () => {},
		support: {},
		setSupport: () => {},
		understandingOfHeartfulness: {},
		setUnderstandingOfHeartfulness: () => {},
		application: {},
		onSubmitClick: () => {},
		onCancelClick: () => {},
		otherReferencePreceptorRecommendation: {},
		setOtherReferencePreceptorRecommendation: () => {},
		otherReferenceRecord: {},
		setOtherReferenceRecord: () => {},
	});

export const OtherReferenceInterviewProvider: FC<Props> = ({
	children,
}: Props) => {
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const [inspirationAndWillingness, setInspirationAndWillingness] =
		useState<Feedback>({});
	const [practice, setPractice] = useState<Feedback>({});
	const [characterAndHabits, setCharacterAndHabits] = useState<Feedback>({});
	const [availabilityAfterProgram, setAvailabilityAfterProgram] =
		useState<Feedback>({});
	const [support, setSupport] = useState<Feedback>({});
	const [understandingOfHeartfulness, setUnderstandingOfHeartfulness] =
		useState<Feedback>({});
	const [application, setApplication] = useState<Partial<Application>>({});
	const [
		otherReferencePreceptorRecommendation,
		setOtherReferencePreceptorRecommendation,
	] = useState<OtherReferenceFeedback>({});
	const [otherReferenceRecord, setOtherReferenceRecord] =
		useState<OtherReferenceFeedback>({});
	const [answertoMandatoryQuestion, setAnswertoMandatoryQuestion] =
		useState<string>('');
	const {
		applicationId,
		navigateWithApplicationId,
		fetchApplication: callFetchApplication,
	} = useApplicationId();
	const { confirmAndSave, onCancelClick, callApi } = useInterviewWizard();
	const { infoPopUpAction } = useInfoPopup();

	const saveFeedback = useCallback(
		async (feedbackData: OtherReferenceFeedback) => {
			const { referenceRecord, review, comments } = feedbackData;
			const feedback: OtherReferenceRecommendationRequest = {
				applicationId,
				feedbackCategory: 'OTHER_REFERENCE_INTERVIEW',
				functionarySrcmId: srcmId as string,
				recommendation: {
					review,
					comments,
				},
				answersToMandatoryQuestions: {
					questionsAndAnswers: [
						{
							categoryId: 905,
							questionId: 955,
							additionalComment: referenceRecord,
						},
					],
				},
				selectedRoleCode: selectedRole as UserRole,
			};

			try {
				await callApi(
					() => submitOtherReferenceFeedback(feedback),
					'errorOccurredWhileSavingFeedback'
				);
				infoPopUpAction(
					'savedSuccessfully',
					'',
					() => {
						navigateWithApplicationId(INTERVIEW_LANDING);
					},
					ConfirmTick
				);
			} catch (e) {
				/* empty */
			}
		},
		[
			applicationId,
			srcmId,
			selectedRole,
			callApi,
			infoPopUpAction,
			navigateWithApplicationId,
		]
	);

	const onSubmitClick = useCallback(
		(feedbackData: OtherReferenceFeedback) => {
			confirmAndSave(() => saveFeedback(feedbackData));
		},
		[confirmAndSave, saveFeedback]
	);

	const value = useMemo<OtherReferenceInterviewContextType>(() => {
		return {
			inspirationAndWillingness,
			setInspirationAndWillingness,
			practice,
			setPractice,
			characterAndHabits,
			setCharacterAndHabits,
			availabilityAfterProgram,
			setAvailabilityAfterProgram,
			support,
			setSupport,
			understandingOfHeartfulness,
			setUnderstandingOfHeartfulness,
			application,
			onSubmitClick,
			onCancelClick,
			otherReferencePreceptorRecommendation,
			setOtherReferencePreceptorRecommendation,
			answertoMandatoryQuestion,
			setAnswertoMandatoryQuestion,
			otherReferenceRecord,
			setOtherReferenceRecord,
		};
	}, [
		inspirationAndWillingness,
		practice,
		characterAndHabits,
		availabilityAfterProgram,
		support,
		understandingOfHeartfulness,
		application,
		onSubmitClick,
		onCancelClick,
		otherReferencePreceptorRecommendation,
		answertoMandatoryQuestion,
		otherReferenceRecord,
	]);

	const fetchApplication = useCallback(async () => {
		try {
			const fetchedApplication = await callFetchApplication();

			setInspirationAndWillingness(
				fetchedApplication.otherReferenceInterviewFeedback
					?.inspirationAndWillingness ?? {}
			);
			setPractice(
				fetchedApplication.otherReferenceInterviewFeedback?.practice ?? {}
			);
			setAvailabilityAfterProgram(
				fetchedApplication.otherReferenceInterviewFeedback
					?.availabilityAfterProgram ?? {}
			);
			setCharacterAndHabits(
				fetchedApplication.otherReferenceInterviewFeedback
					?.characterAndHabits ?? {}
			);
			setSupport(
				fetchedApplication.otherReferenceInterviewFeedback?.support ?? {}
			);
			setUnderstandingOfHeartfulness(
				fetchedApplication.otherReferenceInterviewFeedback
					?.understandingOfHeartfulness ?? {}
			);
			setOtherReferencePreceptorRecommendation(
				fetchedApplication?.otherReferenceRecommendation
			);
			setOtherReferenceRecord((prev) => ({
				...prev,
				referenceRecord:
					fetchedApplication?.otherReferenceInterviewFeedback
						?.answersToMandatoryQuestions?.questionsAndAnswers?.[0]
						?.additionalComment || '',
			}));
			setApplication(fetchedApplication);
		} catch (e) {
			// Do Nothing
		}
	}, [callFetchApplication]);

	useEffect(() => {
		void fetchApplication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<OtherReferenceInterviewContext.Provider value={value}>
			{children}
		</OtherReferenceInterviewContext.Provider>
	);
};

export const useOtherReferenceInterviewWizard = () => {
	return useContext(OtherReferenceInterviewContext);
};
